import React, { useState } from "react";
import { Car, Loader2, Sparkles, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const TrafficTab = ({ stationId, reload }) => {
  const [location, setLocation] = useState("Hamilton");
  const [notes, setNotes] = useState("");
  const [script, setScript] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!location.trim()) return toast.error("Enter a location");
    setBusy(true);
    try {
      const r = await api.post("/newsroom/traffic/script", { location, notes, station_id: stationId });
      setScript(r.data.script);
      toast.success("Traffic update written & saved to your stories");
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Script failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="newsroom-traffic-tab">
      <div className="flex flex-wrap items-start gap-4 mb-4">
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-display text-xl font-bold text-ink-primary">Traffic Center</h2>
          <p className="text-xs text-ink-muted mt-0.5">Presenter-notes powered — the AI never invents incidents or closures</p>
        </div>
        <div className="flex gap-2">
          <input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="newsroom-traffic-location"
            className="rounded-lg border border-line bg-bg-surface px-3 py-2 text-sm text-ink-primary w-44 focus:outline-none focus:border-brand-amber/50" />
          <button onClick={generate} disabled={busy} data-testid="newsroom-traffic-get-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 px-4 py-2.5 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Write Traffic Update
          </button>
        </div>
      </div>

      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200/90 mb-4" data-testid="newsroom-traffic-note">
        Live traffic feeds are paid services — so this desk writes your update from <b>your own presenter notes</b> (listener calls,
        police advisories, what you see on the cams). No notes? You'll get a general commute companion break with zero made-up incidents.
      </div>

      <textarea
        value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} data-testid="newsroom-traffic-notes"
        placeholder={"Presenter notes — e.g.\n• Crash on East Broadway near Crow Lane, one lane blocked\n• Ferry running 15 min late\n• Roadworks on Middle Road until Friday"}
        className="w-full rounded-xl border border-line bg-bg-surface p-4 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/50"
      />

      {script ? (
        <div className="mt-5 rounded-xl border border-line bg-bg-surface p-4" data-testid="newsroom-traffic-script-box">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-orange-300">On-air traffic update · saved to stories</span>
            <button onClick={() => { navigator.clipboard.writeText(script); toast.success("Copied"); }}
              className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink-primary transition">
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
          <div className="whitespace-pre-wrap text-sm text-ink-secondary leading-relaxed">{script}</div>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-dashed border-line p-12 text-center text-sm text-ink-muted">
          <Car className="h-8 w-8 mx-auto mb-2 opacity-40" />
          Enter a location, add your notes, and click "Write Traffic Update"
        </div>
      )}
    </div>
  );
};
